// src/index.js
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";

// Polyfills
import { Buffer } from "buffer";
import process from "process";

// MUI X Pro / Enterprise license
//
// THIS APP RUNS TWO MAJOR VERSIONS OF MUI X AT ONCE, AND THEY KEEP THEIR
// LICENCES IN DIFFERENT PLACES:
//
//   @mui/x-data-grid-pro 6.20.4  ->  reads @mui/x-license-pro  (v6)
//   @mui/x-charts        8.25.0  ->  reads @mui/x-license      (v8)
//
// Setting the key on only `@mui/x-license` put it in the v8 store, which
// DataGridPro never looks at — so every Pro grid in the CRM was running
// unlicensed and showing the watermark, while the key itself was perfectly
// fine. Both stores have to be set, and they take the same key.
import { LicenseInfo } from "@mui/x-license";
import { LicenseInfo as LicenseInfoPro } from "@mui/x-license-pro";

const MUI_X_LICENSE_KEY = process.env.REACT_APP_MUI_X_LICENSE_KEY || "";

LicenseInfo.setLicenseKey(MUI_X_LICENSE_KEY);
LicenseInfoPro.setLicenseKey(MUI_X_LICENSE_KEY);
window.Buffer = Buffer;
window.process = process;

/* ------------------------------------------------------------------ */
/* Mobile shell detection                                              */
/* ------------------------------------------------------------------ */
// The /mobile sub-tree is a completely separate single-page app: no MSAL,
// no desktop AuthProvider, no PageProvider. We detect at boot time whether
// to mount the mobile shell or the full desktop CRM. This is what makes
// the iPhone PWA truly self-contained — adding /mobile to the home screen
// launches a fully-isolated app that never touches the desktop login flow.
//
// We mount the mobile shell when EITHER:
//   1. The current URL begins with /mobile (browser PWA)
//   2. We're inside the Capacitor APK WebView (native shell)
function isMobileShell() {
    try {
        if (typeof window === "undefined") return false;
        if (window.location && window.location.pathname.indexOf("/mobile") === 0) {
            return true;
        }
        const cap = window.Capacitor;
        if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
            return true;
        }
    } catch (e) {
        // ignore
    }
    return false;
}

const root = ReactDOM.createRoot(document.getElementById("root"));

if (isMobileShell()) {
    // ── MOBILE SHELL ─────────────────────────────────────────────
    // Bypass App.jsx, MSAL, AuthProvider, PageProvider entirely.
    // The mobile app uses its own email/password auth via useMobileAuth.
    import("./mobile/MobileApp").then(({ default: MobileApp }) => {
        root.render(
            <React.StrictMode>
                <BrowserRouter>
                    <Routes>
                        <Route path="/mobile/*" element={<MobileApp />} />
                        {/* Anything else inside the mobile shell (e.g. Capacitor
                            booting at "/") is redirected into /mobile. */}
                        <Route path="*" element={<Navigate to="/mobile" replace />} />
                    </Routes>
                </BrowserRouter>
            </React.StrictMode>
        );
    }).catch((err) => {
        console.error("[index] Failed to load MobileApp:", err);
        document.body.innerHTML =
            '<div style="font-family: sans-serif; padding: 24px; text-align: center;">' +
            '<h2>Could not load FIG CRM mobile</h2>' +
            '<p>' + (err && err.message ? err.message : String(err)) + '</p>' +
            '</div>';
    });
} else {
    // ── DESKTOP CRM ──────────────────────────────────────────────
    // Lazy-load the heavy desktop bootstrap so the mobile shell never
    // pays the cost of importing MSAL / AuthProvider / PageProvider.
    Promise.all([
        import("./App"),
        import("./context/AuthContext"),
        import("./context/PageContext"),
        import("@azure/msal-react"),
        import("./config/msalInstance"),
    ]).then(([appModule, authModule, pageModule, msalReactModule, msalInstanceModule]) => {
        const App = appModule.default;
        const { AuthProvider, useAuth } = authModule;
        const { PageProvider } = pageModule;
        const { MsalProvider } = msalReactModule;
        const { msalInstance, ensureMsalInitialized } = msalInstanceModule;

        // Persists desktop route on every navigation, restores on first load.
        function RoutePersistence({ homePath = "/" }) {
            const location = useLocation();
            const navigate = useNavigate();
            const { isAuthenticated } = useAuth();
            const restoredRef = React.useRef(false);

            React.useEffect(() => {
                const path = location.pathname + location.search + location.hash;
                if (location.pathname !== "/login") {
                    localStorage.setItem("lastRoute", path);
                }
            }, [location]);

            React.useEffect(() => {
                if (restoredRef.current) return;
                restoredRef.current = true;
                const atHome =
                    location.pathname === homePath &&
                    location.search === "" &&
                    location.hash === "";
                if (!atHome) return;
                if (!isAuthenticated) return;
                const last = localStorage.getItem("lastRoute");
                if (last && last !== homePath && last !== "/login") {
                    navigate(last, { replace: true });
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, []);

            return null;
        }

        const renderDesktop = () => {
            root.render(
                <React.StrictMode>
                    <BrowserRouter>
                        <MsalProvider instance={msalInstance}>
                            <AuthProvider>
                                <PageProvider>
                                    <RoutePersistence homePath="/" />
                                    <App />
                                </PageProvider>
                            </AuthProvider>
                        </MsalProvider>
                    </BrowserRouter>
                </React.StrictMode>
            );
        };

        ensureMsalInitialized()
            .then(() => {
                console.log("\u2705 MSAL initialized successfully");
                renderDesktop();
            })
            .catch((error) => {
                console.error("\u274C MSAL initialization failed:", error);
                // Fallback: still render — Login component's useMsal() needs the provider.
                renderDesktop();
            });
    });
}
